import type { ReactNode } from 'react';
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  GoogleAuthProvider,
  browserLocalPersistence,
  setPersistence,
  signInWithRedirect,
  getRedirectResult,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  signInWithCustomToken,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from 'firebase/auth';
import type { User as FirebaseUser, UserCredential } from 'firebase/auth';
import {
  doc,
  getDoc,
  getDocFromServer,
  setDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  onSnapshot,
} from 'firebase/firestore';
import { auth, db, functions } from '@/core/firebase';
import { httpsCallable } from 'firebase/functions';
import { revenueCat } from '../lib/revenueCat';
import { posthog } from '@/core/posthog';
import { isNativeShell, prepareRecaptchaContainer } from '@/core/capacitor';
import { clearPendingTrainerInvite, getPendingTrainerInvite } from '../lib/trainerInvite';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { cancelAllNotifications } from '@/core/notifications';
import type { Client, User, UserRole, SessionLog, WorkoutAssignment } from '../types';

// Debug logger that writes to PhoneLogin's debug panel if available
const debugLog = (msg: string) => {
  try {
    console.log(msg);
    if (typeof window !== 'undefined' && (window as any).__phoneLoginDebug) {
      (window as any).__phoneLoginDebug(msg);
    }
  } catch (e) {
    console.error('debugLog failed:', e);
  }
};

interface AuthContextType {
  user: FirebaseUser | null;
  userData: User | null;
  loading: boolean;
  pendingTrainerInvites: PendingTrainerInvite[];
  pendingTrainerInvitesLoading: boolean;
  upcomingAssignments: WorkoutAssignment[];
  createSessionLog: (log: Omit<SessionLog, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  signInWithGoogle: () => Promise<void>;
  signInWithPhone: (phoneNumber: string) => Promise<PhoneConfirmationResult>;
  signInWithPhoneUsingVerifier: (phoneNumber: string, verifier: RecaptchaVerifier) => Promise<PhoneConfirmationResult>;
  verifyPhoneCode: (confirmation: PhoneConfirmationResult, code: string) => Promise<void>;
  signInAsTrainer: (secretToken: string) => Promise<void>;
  signInAsClient: (secretToken: string) => Promise<void>;
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  setSkipAutoCreateUser: (skip: boolean) => void;
  refreshPendingTrainerInvites: (phoneOverride?: string) => Promise<PendingTrainerInvite[]>;
  refreshUserData: () => Promise<User | null>;
  acceptPendingTrainerInvite: (inviteId: string) => Promise<void>;
  declinePendingTrainerInvite: (inviteId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type WindowWithRecaptcha = Window & {
  recaptchaVerifier?: RecaptchaVerifier | null;
}

const AUTH_STORAGE_KEY_PATTERNS = ['firebase:authUser', 'firebase:pendingRedirect'];

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const clearMatchingStorageKeys = (storage: Storage) => {
  Object.keys(storage)
    .filter((key) => AUTH_STORAGE_KEY_PATTERNS.some((pattern) => key.includes(pattern)))
    .forEach((key) => storage.removeItem(key));
};

const listFirebaseAuthDatabaseNames = async (): Promise<string[]> => {
  if (typeof indexedDB === 'undefined') {
    return [];
  }

  const maybeDatabases = (indexedDB as IDBFactory & {
    databases?: () => Promise<Array<{ name?: string }>>
  }).databases;

  if (!maybeDatabases) {
    return [];
  }

  const databaseNames = await maybeDatabases.call(indexedDB).catch(() => []);
  return databaseNames
    .map((database) => database.name)
    .filter((name): name is string => Boolean(name))
    .filter((name) => /firebase.*localstorage/i.test(name));
};

const deleteIndexedDbDatabase = async (name: string): Promise<boolean> =>
  new Promise((resolve) => {
    const request = indexedDB.deleteDatabase(name);
    request.onsuccess = () => resolve(true);
    request.onerror = () => resolve(false);
    request.onblocked = () => resolve(false);
  });

const clearBrowserAuthArtifacts = async () => {
  if (typeof window === 'undefined') {
    return;
  }

  const authWindow = window as WindowWithRecaptcha;
  if (authWindow.recaptchaVerifier) {
    authWindow.recaptchaVerifier.clear();
    authWindow.recaptchaVerifier = null;
  }

  clearMatchingStorageKeys(window.localStorage);
  clearMatchingStorageKeys(window.sessionStorage);

  for (let attempt = 1; attempt <= 10; attempt++) {
    const authDatabases = await listFirebaseAuthDatabaseNames();
    if (authDatabases.length === 0) {
      return;
    }

    await Promise.all(authDatabases.map(deleteIndexedDbDatabase));

    const remainingDatabases = await listFirebaseAuthDatabaseNames();
    if (remainingDatabases.length === 0) {
      return;
    }

    if (attempt < 10) {
      await delay(100 * attempt);
    }
  }

  const remainingDatabases = await listFirebaseAuthDatabaseNames();
  if (remainingDatabases.length > 0) {
    console.warn('[AuthContext] Firebase auth IndexedDB persisted after sign out:', remainingDatabases);
  }
};

const waitForAuthState = (expectedUser: FirebaseUser | null, timeout = 10000): Promise<void> =>
  new Promise((resolve, reject) => {
    const matchesCurrentUser = () =>
      (expectedUser === null && auth.currentUser === null) ||
      (expectedUser !== null && auth.currentUser?.uid === expectedUser.uid);

    if (matchesCurrentUser()) {
      resolve();
      return;
    }

    const timeoutId = window.setTimeout(() => {
      unsubscribe();
      reject(new Error(`Timed out waiting for auth state: ${expectedUser?.uid || 'null'}`));
    }, timeout);

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      const matchesExpectedUser =
        (expectedUser === null && firebaseUser === null) ||
        (expectedUser !== null && firebaseUser?.uid === expectedUser.uid);

      if (!matchesExpectedUser) {
        return;
      }

      window.clearTimeout(timeoutId);
      unsubscribe();
      resolve();
    });
  });

export type PhoneConfirmationResult = {
  kind: 'web' | 'native'
  verificationId?: string  // For native flow
  confirm: (code: string) => Promise<UserCredential>
}

export type PendingTrainerInvite = Client & {
  trainerName: string
}

type AuthClaims = {
  trainerId?: string
  role?: UserRole
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingTrainerInvites, setPendingTrainerInvites] = useState<PendingTrainerInvite[]>([]);
  const [pendingTrainerInvitesLoading, setPendingTrainerInvitesLoading] = useState(false);
  const [upcomingAssignments, setUpcomingAssignments] = useState<WorkoutAssignment[]>([]);
  const isDeletingRef = React.useRef(false);
  const skipAutoCreateUserRef = React.useRef(false);

  const detectTimezone = (): string => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  const inferClientOnboardingComplete = (candidate: Partial<User>): boolean => {
    if (candidate.role !== 'client') {
      return false;
    }

    if (candidate.onboardingComplete === true || candidate.introSessionBooked === true) {
      return true;
    }

    return Boolean(
      candidate.displayName?.trim() &&
      candidate.fitnessGoal &&
      candidate.experienceLevel &&
      candidate.equipment?.length &&
      candidate.preferredDuration
    );
  };

  const resolveUserRole = (claims?: AuthClaims): UserRole => {
    if (claims?.role === 'trainer' || claims?.trainerId) {
      return 'trainer';
    }

    return 'client';
  };

  const buildUserData = (
    id: string,
    data: Omit<User, 'id'>,
    authClaims?: AuthClaims,
    trainerFromClient?: string
  ): User => {
    const inferredOnboardingComplete = inferClientOnboardingComplete(data);

    return {
      id,
      ...data,
      onboardingComplete: inferredOnboardingComplete,
      trainerId: data.trainerId || trainerFromClient || authClaims?.trainerId,
    };
  };

  const assignClientToTrainerFromInvite = async (userRecord: User): Promise<User> => {
    if (userRecord.role !== 'client') {
      clearPendingTrainerInvite();
      return userRecord;
    }

    const pendingTrainerId = getPendingTrainerInvite();
    if (!pendingTrainerId) {
      return userRecord;
    }

    if (userRecord.trainerId && userRecord.trainerId !== pendingTrainerId) {
      clearPendingTrainerInvite();
      return userRecord;
    }

    const getTrainerInvitePreview = httpsCallable<
      { trainerId: string },
      { valid: boolean; trainerId?: string; trainerName?: string }
    >(functions, 'getTrainerInvitePreview')
    const preview = await getTrainerInvitePreview({ trainerId: pendingTrainerId })
    if (!preview.data.valid) {
      clearPendingTrainerInvite();
      return userRecord;
    }

    const materializeTrainerInviteFromLink = httpsCallable<
      { trainerId: string },
      { status: 'pending' | 'linked'; trainerId: string; inviteId?: string }
    >(functions, 'materializeTrainerInviteFromLink')
    const result = await materializeTrainerInviteFromLink({ trainerId: pendingTrainerId })

    clearPendingTrainerInvite();

    if (result.data.status === 'linked') {
      return {
        ...userRecord,
        trainerId: pendingTrainerId,
      };
    }

    return userRecord;
  };

  // Load user doc using Firestore SDK
  const loadUserDoc = async (
    firebaseUser: FirebaseUser,
    authClaims?: AuthClaims
  ): Promise<User | null> => {
    const uid = firebaseUser.uid;
    debugLog(`[Firestore] Loading user doc for ${uid}`);
    
    const userDocRef = doc(db, 'users', uid);
    const startTime = Date.now();
    
    try {
      const fetchUserDoc = async () => {
        try {
          return await getDocFromServer(userDocRef);
        } catch (serverError: any) {
          debugLog(`[Firestore] Server getDoc failed, falling back to cache: ${serverError?.message}`);
          return getDoc(userDocRef);
        }
      };

      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error(`Firestore getDoc timeout after 15s`));
        }, 15000);
      });
      
      const userDoc = await Promise.race([
        fetchUserDoc(),
        timeoutPromise
      ]);
      
      const elapsed = Date.now() - startTime;
      debugLog(
        `[Firestore] ✅ getDoc completed in ${elapsed}ms, exists=${userDoc.exists()}, fromCache=${userDoc.metadata.fromCache}`
      );
      
      if (userDoc.exists()) {
        const data = userDoc.data() as Omit<User, 'id'>;
        let trainerFromClient: string | undefined;

        if (data.role === 'client' && !data.trainerId) {
          try {
            const linkedClientQuery = query(
              collection(db, 'clients'),
              where('userId', '==', uid),
              where('status', '==', 'linked')
            );
            const linkedClientSnapshot = await getDocs(linkedClientQuery);
            trainerFromClient = linkedClientSnapshot.empty
              ? undefined
              : (linkedClientSnapshot.docs[0].data().trainerId as string | undefined);
          } catch (clientErr) {
            console.warn('[AuthContext] Could not read client doc:', clientErr);
          }
        }

        let userDataObj = buildUserData(userDoc.id, data, authClaims, trainerFromClient);

        if (!data.timezone) {
          const timezone = detectTimezone();
          userDataObj.timezone = timezone;
          setDoc(userDocRef, { timezone }, { merge: true }).catch(err => {
            console.warn('[AuthContext] Failed to save timezone:', err);
          });
        }

        if (!data.onboardingComplete && userDataObj.onboardingComplete) {
          setDoc(userDocRef, { onboardingComplete: true }, { merge: true }).catch((err) => {
            console.warn('[AuthContext] Failed to repair onboardingComplete:', err);
          });
        }

        userDataObj = await assignClientToTrainerFromInvite(userDataObj);

        return userDataObj;
      } else {
        debugLog(`[Firestore] User doc does not exist`);
        
        if (isDeletingRef.current || skipAutoCreateUserRef.current) {
          return null;
        }
        
        debugLog('[Firestore] Creating new user doc...');
        const timezone = detectTimezone();
        const role = resolveUserRole(authClaims);
        const newUserData: Omit<User, 'id'> = {
          role,
          displayName: firebaseUser.displayName || '',
          email: firebaseUser.email || '',
          phone: firebaseUser.phoneNumber || '',
          ...(authClaims?.trainerId ? { trainerId: authClaims.trainerId } : {}),
          timezone,
          createdAt: serverTimestamp() as any,
        };

        await setDoc(userDocRef, newUserData, { merge: true });
        debugLog('[Firestore] ✅ User doc created');
        
        return assignClientToTrainerFromInvite({ id: uid, ...newUserData } as User);
      }
    } catch (err: any) {
      const elapsed = Date.now() - startTime;
      debugLog(`[Firestore] ❌ Failed after ${elapsed}ms: ${err?.message}`);
      return null;
    }
  };

  const fetchUpcomingAssignments = async (userId: string) => {
    try {
      const now = new Date();
      const endDate = new Date(now);
      endDate.setDate(now.getDate() + 7);

      const assignmentsQuery = query(
        collection(db, 'workoutAssignments'),
        where('clientId', '==', userId),
        where('status', 'in', ['pending', 'reminded', 'confirmed']),
        where('scheduledFor', '>=', now),
        where('scheduledFor', '<=', endDate)
      );

      const snapshot = await getDocs(assignmentsQuery);
      const assignments = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as WorkoutAssignment));

      assignments.sort((a, b) => {
        const aTime = (a.scheduledFor as any).toDate?.()?.getTime() || 0;
        const bTime = (b.scheduledFor as any).toDate?.()?.getTime() || 0;
        return aTime - bTime;
      });

      setUpcomingAssignments(assignments);
    } catch (error) {
      console.error('Error fetching upcoming assignments:', error);
      setUpcomingAssignments([]);
    }
  };

  const refreshPendingTrainerInvites = async (phoneOverride?: string): Promise<PendingTrainerInvite[]> => {
    const phone = phoneOverride || auth.currentUser?.phoneNumber || user?.phoneNumber || userData?.phone;

    if (!phone) {
      setPendingTrainerInvites([]);
      setPendingTrainerInvitesLoading(false);
      return [];
    }

    setPendingTrainerInvitesLoading(true);

    try {
      const invitesQuery = query(
        collection(db, 'clients'),
        where('phone', '==', phone),
        where('status', '==', 'pending')
      );
      const invitesSnapshot = await getDocs(invitesQuery);

      if (invitesSnapshot.empty) {
        setPendingTrainerInvites([]);
        return [];
      }

      const invites = invitesSnapshot.docs.map((docSnap) => {
        const data = docSnap.data() as Omit<Client, 'id'> & { trainerName?: string };
        return {
          id: docSnap.id,
          ...data,
          trainerName: data.trainerName || 'A trainer',
        };
      }) as PendingTrainerInvite[];

      setPendingTrainerInvites(invites);
      return invites;
    } finally {
      setPendingTrainerInvitesLoading(false);
    }
  };

  const acceptPendingTrainerInvite = async (inviteId: string) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No signed-in user');
    }

    const invite = pendingTrainerInvites.find((item) => item.id === inviteId);
    if (!invite) {
      throw new Error('Invite not found');
    }

    // Call Cloud Function
    const acceptFn = httpsCallable<{ inviteId: string }, { success: boolean; trainerId: string }>(
      functions,
      'acceptTrainerInvite'
    );
    
    const result = await acceptFn({ inviteId });

    // Update local state
    setUserData((prev) => (prev ? { ...prev, trainerId: result.data.trainerId, phone: currentUser.phoneNumber || invite.phone } : prev));
    setPendingTrainerInvites([]);
  };

  const declinePendingTrainerInvite = async (inviteId: string) => {
    // Call Cloud Function
    const declineFn = httpsCallable<{ inviteId: string }, { success: boolean }>(
      functions,
      'declineTrainerInvite'
    );
    
    await declineFn({ inviteId });

    // Update local state
    setPendingTrainerInvites((prev) => prev.filter((invite) => invite.id !== inviteId));
  };

  useEffect(() => {
    getRedirectResult(auth).catch((err) => {
      console.warn('Redirect sign-in failed', err);
    });

    let userDocUnsubscribe: (() => void) | null = null;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      debugLog(`[AuthContext] onAuthStateChanged: ${firebaseUser?.uid || 'null'}`);
      userDocUnsubscribe?.();
      userDocUnsubscribe = null;

      setUser(firebaseUser);

      if (firebaseUser) {
        revenueCat.prepareForUser(firebaseUser.uid).catch(err => {
          console.warn('[AuthContext] RevenueCat startup failed:', err);
        });

        let authClaims: AuthClaims | undefined;
        try {
          const tokenResult = await firebaseUser.getIdTokenResult();
          authClaims = {
            trainerId: tokenResult.claims.trainerId as string | undefined,
            role: tokenResult.claims.role as UserRole | undefined,
          };
        } catch (tokenErr: any) {
          debugLog(`[AuthContext] ⚠️ Failed to get token result: ${tokenErr?.message}`);
        }

        const loadedUserData = await loadUserDoc(firebaseUser, authClaims);
        
        if (loadedUserData) {
          setUserData(loadedUserData);
          userDocUnsubscribe = onSnapshot(
            doc(db, 'users', firebaseUser.uid),
            async (userSnap) => {
              if (!userSnap.exists()) {
                setUserData(null);
                return;
              }

              const data = userSnap.data() as Omit<User, 'id'>;
              let trainerFromClient: string | undefined;

              if (data.role === 'client' && !data.trainerId) {
                try {
                  const linkedClientQuery = query(
                    collection(db, 'clients'),
                    where('userId', '==', firebaseUser.uid),
                    where('status', '==', 'linked')
                  );
                  const linkedClientSnapshot = await getDocs(linkedClientQuery);
                  trainerFromClient = linkedClientSnapshot.empty
                    ? undefined
                    : (linkedClientSnapshot.docs[0].data().trainerId as string | undefined);
                } catch (clientErr) {
                  console.warn('[AuthContext] Could not read linked client doc from live user subscription:', clientErr);
                }
              }

              setUserData(buildUserData(userSnap.id, data, authClaims, trainerFromClient));
            },
            (err) => {
              console.warn('[AuthContext] User doc subscription failed:', err);
            }
          );
          posthog.identify(firebaseUser.uid, {
            role: loadedUserData.role,
            displayName: loadedUserData.displayName,
            trainerId: loadedUserData.trainerId,
          });
          fetchUpcomingAssignments(firebaseUser.uid).catch(() => {});
          refreshPendingTrainerInvites(firebaseUser.phoneNumber || loadedUserData.phone).catch((err) => {
            console.warn('[AuthContext] Failed to refresh pending invites:', err);
          });
        } else {
          setUserData(null);
          setUpcomingAssignments([]);
          setPendingTrainerInvites([]);
        }

        setLoading(false);
      } else {
        setUserData(null);
        setUpcomingAssignments([]);
        setPendingTrainerInvites([]);
        setPendingTrainerInvitesLoading(false);
        setLoading(false);
        isDeletingRef.current = false;
      }
    });

    return () => {
      userDocUnsubscribe?.();
      unsubscribe();
    };
  }, []);

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await setPersistence(auth, browserLocalPersistence);
      await signInWithRedirect(auth, provider);
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  const signInWithPhone = async (phoneNumber: string): Promise<PhoneConfirmationResult> => {
    debugLog(`[Auth] signInWithPhone: ${phoneNumber}`);
    
    const useNative = isNativeShell();
    debugLog(`[Auth] isNativeShell: ${useNative}`);
    
    if (useNative) {
      debugLog(`[Auth] NATIVE path`);
      
      return new Promise<PhoneConfirmationResult>((resolve, reject) => {
        let resolved = false;
        const handles: Array<{ remove: () => Promise<void> }> = [];
        
        const timeout = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            handles.forEach(h => h.remove().catch(() => {}));
            reject(new Error('Native phone auth timed out'));
          }
        }, 30000);

        FirebaseAuthentication.addListener('phoneCodeSent', (event) => {
          debugLog(`[Auth] 📱 phoneCodeSent: ${JSON.stringify(event)}`);
          
          if (resolved) return;
          resolved = true;
          clearTimeout(timeout);
          handles.forEach(h => h.remove().catch(() => {}));

          const { verificationId } = event as { verificationId: string };
          
          // Return a confirmation result that uses NATIVE flow
          resolve({
            kind: 'native',
            verificationId,
            confirm: async (code: string): Promise<UserCredential> => {
              debugLog(`[Auth] Native confirm: using confirmVerificationCode`);
              
              // Step 1: Confirm via native plugin (signs in native SDK)
              debugLog(`[Auth] Step 1: confirmVerificationCode on native`);
              await FirebaseAuthentication.confirmVerificationCode({
                verificationId,
                verificationCode: code,
              });
              debugLog(`[Auth] ✅ Native auth confirmed`);
              
              // Step 2: Get ID token from native SDK
              debugLog(`[Auth] Step 2: getIdToken from native`);
              const tokenResult = await FirebaseAuthentication.getIdToken({ forceRefresh: true });
              const nativeIdToken = tokenResult.token;
              debugLog(`[Auth] ✅ Got native ID token (length: ${nativeIdToken.length})`);
              
              // Step 3: Exchange native token for custom token via Cloud Function
              debugLog(`[Auth] Step 3: exchangeNativeToken via Cloud Function`);
              const exchangeFn = httpsCallable(functions, 'exchangeNativeToken');
              const exchangeResult = await exchangeFn({ idToken: nativeIdToken });
              const { customToken } = exchangeResult.data as { customToken: string };
              debugLog(`[Auth] ✅ Got custom token`);
              
              // Step 4: Sign in web SDK with custom token
              debugLog(`[Auth] Step 4: signInWithCustomToken on web SDK`);
              const userCredential = await signInWithCustomToken(auth, customToken);
              debugLog(`[Auth] ✅ Web SDK signed in: ${userCredential.user.uid}`);
              
              return userCredential;
            },
          });
        }).then(handle => {
          handles.push(handle);
        });

        FirebaseAuthentication.addListener('phoneVerificationFailed', (event) => {
          debugLog(`[Auth] ❌ phoneVerificationFailed: ${JSON.stringify(event)}`);
          
          if (resolved) return;
          resolved = true;
          clearTimeout(timeout);
          handles.forEach(h => h.remove().catch(() => {}));

          const { message } = event as { message?: string };
          reject(new Error(message || 'Phone verification failed'));
        }).then(handle => {
          handles.push(handle);
        });

        FirebaseAuthentication.signInWithPhoneNumber({
          phoneNumber,
          skipNativeAuth: false,
        }).catch(err => {
          if (!resolved) {
            resolved = true;
            clearTimeout(timeout);
            handles.forEach(h => h.remove().catch(() => {}));
            reject(err);
          }
        });
      });
    }

    // Web path
    debugLog(`[Auth] WEB path`);
    try {
      await setPersistence(auth, browserLocalPersistence);
      prepareRecaptchaContainer('recaptcha-container', false);

      const authWindow = window as WindowWithRecaptcha;
      if (authWindow.recaptchaVerifier) {
        authWindow.recaptchaVerifier.clear();
        authWindow.recaptchaVerifier = null;
      }

      const recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
      });
      authWindow.recaptchaVerifier = recaptchaVerifier;

      const confirmation = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
      return {
        kind: 'web',
        confirm: (code: string) => confirmation.confirm(code),
      };
    } catch (error: any) {
      debugLog(`[Auth] ❌ Web auth error: ${error?.message}`);
      throw error;
    }
  };

  const signInWithPhoneUsingVerifier = async (
    phoneNumber: string,
    verifier: RecaptchaVerifier
  ): Promise<PhoneConfirmationResult> => {
    await setPersistence(auth, browserLocalPersistence);
    const confirmation = await signInWithPhoneNumber(auth, phoneNumber, verifier);
    return {
      kind: 'web',
      confirm: (code: string) => confirmation.confirm(code),
    };
  };

  const verifyPhoneCode = async (confirmation: PhoneConfirmationResult, code: string): Promise<void> => {
    debugLog('[Auth] verifyPhoneCode starting');
    debugLog(`[Auth] confirmation.kind: ${confirmation.kind}`);
    
    const result = await confirmation.confirm(code);
    const firebaseUser = result.user;
    debugLog(`[Auth] ✅ Auth confirmed, uid: ${firebaseUser.uid}`);

    // Force token refresh
    await firebaseUser.getIdToken(true);

    // Load user doc
    let authClaims: AuthClaims | undefined;
    try {
      const tokenResult = await firebaseUser.getIdTokenResult();
      authClaims = {
        trainerId: tokenResult.claims.trainerId as string | undefined,
        role: tokenResult.claims.role as UserRole | undefined,
      };
    } catch {
      debugLog('[Auth] Token claims unavailable after phone verification');
    }
    
    const loadedUserData = await loadUserDoc(firebaseUser, authClaims);
    if (loadedUserData) {
      setUserData(loadedUserData);
      await refreshPendingTrainerInvites(firebaseUser.phoneNumber || loadedUserData.phone);
      debugLog(`[Auth] ✅ User data loaded: role=${loadedUserData.role}`);
    } else {
      setPendingTrainerInvites([]);
      debugLog(`[Auth] ⚠️ Could not load user data`);
    }
    setLoading(false);
  };

  const exchangeTokenAndSignIn = async (
    secretToken: string,
    endpoint: 'exchangeTrainerToken' | 'exchangeClientToken'
  ) => {
    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
    const functionsBase =
      import.meta.env.VITE_FUNCTIONS_BASE_URL ||
      `https://us-central1-${projectId || 'grounded-fitness'}.cloudfunctions.net`;

    const response = await fetch(`${functionsBase}/${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ secretToken }),
    });

    if (!response.ok) {
      throw new Error(`Invalid token`);
    }

    const { customToken } = await response.json();
    await signInWithCustomToken(auth, customToken);
  };

  const signInAsTrainer = (secretToken: string) =>
    exchangeTokenAndSignIn(secretToken, 'exchangeTrainerToken');

  const signInAsClient = (secretToken: string) =>
    exchangeTokenAndSignIn(secretToken, 'exchangeClientToken');

  const signOut = async () => {
    try {
      await cancelAllNotifications();
      if (isNativeShell()) {
        FirebaseAuthentication.signOut().catch(() => {});
      }
      await revenueCat.logout();
      posthog.reset();
      await firebaseSignOut(auth);
      await waitForAuthState(null);
      setUser(null);
      setUserData(null);
      setUpcomingAssignments([]);
      setPendingTrainerInvites([]);
      setPendingTrainerInvitesLoading(false);
      await clearBrowserAuthArtifacts();
      await delay(150);
      await clearBrowserAuthArtifacts();
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const deleteAccount = async () => {
    if (!user || !userData) {
      throw new Error('No user signed in');
    }

    try {
      isDeletingRef.current = true;
      const deleteAccountFn = httpsCallable(functions, 'deleteAccount');
      await deleteAccountFn();
    } catch (error) {
      isDeletingRef.current = false;
      throw error;
    }
  };

  const setSkipAutoCreateUser = (skip: boolean) => {
    skipAutoCreateUserRef.current = skip;
  };

  const refreshUserData = async (): Promise<User | null> => {
    const currentUser = auth.currentUser || user;
    if (!currentUser) return null;

    const tokenResult = await currentUser.getIdTokenResult(true);
    const authClaims = tokenResult.claims as AuthClaims;
    const refreshedData = await loadUserDoc(currentUser, authClaims);
    setUser(currentUser);
    setUserData(refreshedData);
    return refreshedData;
  };

  const createSessionLog = async (log: Omit<SessionLog, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    if (!user) throw new Error('No user signed in');

    const logData = {
      ...log,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, 'sessionLogs'), logData);
    return docRef.id;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userData,
        loading,
        pendingTrainerInvites,
        pendingTrainerInvitesLoading,
        upcomingAssignments,
        createSessionLog,
        signInWithGoogle,
        signInWithPhone,
        signInWithPhoneUsingVerifier,
        verifyPhoneCode,
        signInAsTrainer,
        signInAsClient,
        signOut,
        deleteAccount,
        setSkipAutoCreateUser,
        refreshUserData,
        refreshPendingTrainerInvites,
        acceptPendingTrainerInvite,
        declinePendingTrainerInvite,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
